interface TutorialStep {
  id: string;
  title: string;
  description: string;
}

interface ParentQuest {
  id: string;
  title: string;
  steps: TutorialStep[];
}

interface TutorialState {
  [key: string]: boolean;
}

export class TutorialSystem {
  private overlay: HTMLElement | null;
  private questPanel: HTMLElement | null;
  private questList: HTMLElement | null;
  private currentBubble: HTMLElement | null = null;
  private tutorialState: TutorialState;
  private quests: ParentQuest[];

  constructor() {
    this.overlay = document.getElementById('tutorial-overlay');
    this.questPanel = document.getElementById('tutorial-quest-panel');
    this.questList = document.getElementById('tutorial-quest-list');
    this.tutorialState = this.loadTutorialState();
    this.quests = [
      {
        id: 'getting-started',
        title: 'Getting Started',
        steps: [
          { id: 'open-help', title: 'Open Help', description: 'Learn how to access help at any time' }
        ]
      },
      {
        id: 'explore-cameras',
        title: 'Explore: Learn Camera Types',
        steps: [
          { id: 'camera-free', title: 'Free Camera Controls', description: 'Learn to move with mouse + WASD' },
          { id: 'camera-orbit', title: 'Orbit Camera Controls', description: 'Learn to orbit around the planet' },
          { id: 'camera-rts', title: 'RTS Camera Controls', description: 'Learn RTS-style camera movement' }
        ]
      },
      {
        id: 'documentation',
        title: 'Documentation',
        steps: [
          { id: 'view-docs', title: 'View Documentation', description: 'Access the full documentation for more details' },
          { id: 'view-api', title: 'View API Reference', description: 'Access the API documentation for developers' }
        ]
      }
    ];

    this.init();
  }

  private loadTutorialState(): TutorialState {
    try {
      const saved = localStorage.getItem('tutorialState');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  }

  private saveTutorialState(): void {
    try {
      localStorage.setItem('tutorialState', JSON.stringify(this.tutorialState));
    } catch (e) {
      console.warn('Failed to save tutorial state:', e);
    }
  }

  private isQuestCompleted(questId: string): boolean {
    return this.tutorialState[questId] === true;
  }

  private isStepCompleted(stepId: string): boolean {
    return this.tutorialState[stepId] === true;
  }

  private isParentQuestCompleted(parentQuest: ParentQuest): boolean {
    // A parent quest is completed if all its steps are completed
    return parentQuest.steps.every(step => this.isStepCompleted(step.id));
  }

  private completeQuest(questId: string): void {
    if (!this.isQuestCompleted(questId)) {
      this.tutorialState[questId] = true;
      this.saveTutorialState();
      this.updateQuestPanel();
    }
  }

  private completeStep(stepId: string): void {
    if (!this.isStepCompleted(stepId)) {
      this.tutorialState[stepId] = true;
      this.saveTutorialState();
      this.updateQuestPanel();
    }
  }

  public resetTutorial(): void {
    this.tutorialState = {};
    this.saveTutorialState();
    this.updateQuestPanel();
    this.hideBubble();
  }

  private init(): void {
    // Help button - toggle panel
    const helpBtn = document.getElementById('help');
    if (helpBtn) {
      helpBtn.addEventListener('click', () => {
        this.toggleTutorial();
      });
    }

    // Close panel button
    const closePanelBtn = this.questPanel?.querySelector('.close-panel-btn');
    if (closePanelBtn) {
      closePanelBtn.addEventListener('click', () => {
        this.hideTutorial();
      });
    }

    this.updateQuestPanel();
    this.updateButtonText();

    // Listen for camera mode changes
    window.addEventListener('cameraModeChanged', (e: Event) => {
      const customEvent = e as CustomEvent<{ mode: 'free' | 'orbit' | 'rts' }>;
      this.handleCameraModeChange(customEvent.detail.mode);
    });

    // Show initial tutorial after a short delay to let the game load
    setTimeout(() => {
      this.showInitialTutorial();
    }, 500);
  }

  private showInitialTutorial(): void {
    // Show the "open-help" tutorial if not completed
    if (!this.isStepCompleted('open-help')) {
      this.showOpenHelpBubble();
    }
  }

  private toggleTutorial(): void {
    if (this.questPanel?.classList.contains('visible')) {
      this.hideTutorial();
    } else {
      this.showTutorial();
    }
    this.updateButtonText();
  }

  private updateButtonText(): void {
    const helpBtn = document.getElementById('help');
    if (helpBtn) {
      const span = helpBtn.querySelector('span');
      if (span) {
        // Button text stays as "Help" - no need to change it
        // The panel visibility is enough indication
      }
    }
  }

  private hideTutorial(): void {
    this.questPanel?.classList.remove('visible');
    this.hideBubble();
    this.updateButtonText();
  }

  private showTutorial(): void {
    this.questPanel?.classList.add('visible');
    this.updateButtonText();
    // Show initial tutorial based on current camera mode
    // Wait a bit for game to be ready, then check camera mode
    setTimeout(() => {
      const gameInstance = (window as any).gameInstance;
      if (gameInstance && gameInstance.getCameraMode) {
        const currentMode = gameInstance.getCameraMode();
        if (currentMode) {
          this.handleCameraModeChange(currentMode);
        }
      } else {
        // Default to free camera if game not ready yet
        this.handleCameraModeChange('free');
      }
    }, 100);
  }

  private handleCameraModeChange(mode: 'free' | 'orbit' | 'rts'): void {
    // Only show if tutorial panel is visible
    if (!this.questPanel?.classList.contains('visible')) {
      return;
    }

    const stepId = `camera-${mode}`;

    // Don't show if already completed
    if (this.isStepCompleted(stepId)) {
      return;
    }

    // Show appropriate help bubble
    this.showCameraModeBubble(mode);
  }

  private showCameraModeBubble(mode: 'free' | 'orbit' | 'rts', isFromClick: boolean = false): void {
    this.hideBubble();

    if (!this.overlay) return;

    const bubble = document.createElement('div');
    bubble.className = 'help-bubble';

    let title: string, content: string, controls: string;

    switch (mode) {
      case 'free':
        title = 'Free Camera Mode';
        content = 'You are in <strong>Free Camera</strong> mode. Move freely in 3D space!';
        controls = `
          <div class="controls">
            <p><strong>Controls:</strong></p>
            <p>🖱️ <strong>Mouse:</strong> Look around</p>
            <p><kbd>W</kbd> <kbd>A</kbd> <kbd>S</kbd> <kbd>D</kbd> Move forward/left/back/right</p>
            <p><kbd>X</kbd> Move down | <kbd>C</kbd> Move up</p>
          </div>
        `;
        break;
      case 'orbit':
        title = 'Orbit Camera Mode';
        content = 'You are in <strong>Orbit Camera</strong> mode. Orbit around the planet!';
        controls = `
          <div class="controls">
            <p><strong>Controls:</strong></p>
            <p>🖱️ <strong>Left Click + Drag:</strong> Rotate around planet</p>
            <p>🖱️ <strong>Right Click + Drag:</strong> Adjust camera view angle</p>
            <p>🖱️ <strong>Scroll:</strong> Zoom in/out</p>
            <p><kbd>W</kbd> <kbd>S</kbd> Change elevation angle</p>
            <p><kbd>A</kbd> <kbd>D</kbd> Rotate left/right</p>
            <p><kbd>X</kbd> Zoom in | <kbd>C</kbd> Zoom out</p>
          </div>
        `;
        break;
      case 'rts':
        title = 'RTS Camera Mode';
        content = 'You are in <strong>RTS Camera</strong> mode. Classic strategy game view!';
        controls = `
          <div class="controls">
            <p><strong>Controls:</strong></p>
            <p>🖱️ <strong>Left Click + Drag:</strong> Pan camera</p>
            <p>🖱️ <strong>Scroll:</strong> Zoom in/out</p>
            <p><kbd>W</kbd> <kbd>A</kbd> <kbd>S</kbd> <kbd>D</kbd> Pan camera</p>
            <p><kbd>X</kbd> Zoom in | <kbd>C</kbd> Zoom out</p>
          </div>
        `;
        break;
    }

    const stepId = `camera-${mode}`;
    const isCompleted = this.isStepCompleted(stepId);

    bubble.innerHTML = `
      <div class="close-btn" title="${isCompleted ? 'Close' : 'Mark as complete'}">×</div>
      <h3>${title}</h3>
      <p>${content}</p>
      ${controls}
    `;

    // Position bubble (top-left area)
    bubble.style.top = '120px';
    bubble.style.left = '20px';

    // Close button handler
    const closeBtn = bubble.querySelector('.close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        // Only mark as complete if not already completed
        if (!isCompleted) {
          this.completeStep(stepId);
        }
        this.hideBubble();
      });
    }

    this.overlay.appendChild(bubble);
    this.currentBubble = bubble;
  }

  private showBubbleForStep(stepId: string): void {
    // Ensure panel is visible when clicking a quest step
    if (!this.questPanel?.classList.contains('visible')) {
      this.questPanel?.classList.add('visible');
      this.updateButtonText();
    }

    // Handle different step types
    if (stepId === 'open-help') {
      this.showOpenHelpBubble();
    } else if (stepId === 'view-docs') {
      this.showViewDocsBubble();
      // Auto-complete when clicked
      if (!this.isStepCompleted(stepId)) {
        this.completeStep(stepId);
      }
    } else if (stepId === 'view-api') {
      this.showViewApiBubble();
      // Auto-complete when clicked
      if (!this.isStepCompleted(stepId)) {
        this.completeStep(stepId);
      }
    } else if (stepId.startsWith('camera-')) {
      const mode = stepId.replace('camera-', '') as 'free' | 'orbit' | 'rts';
      if (mode === 'free' || mode === 'orbit' || mode === 'rts') {
        this.showCameraModeBubble(mode, true);
      }
    }
  }

  private showOpenHelpBubble(): void {
    this.hideBubble();

    if (!this.overlay) return;

    const bubble = document.createElement('div');
    bubble.className = 'help-bubble';

    const stepId = 'open-help';
    const isCompleted = this.isStepCompleted(stepId);

    bubble.innerHTML = `
      <div class="close-btn" title="${isCompleted ? 'Close' : 'Mark as complete'}">×</div>
      <h3>Welcome! Getting Help</h3>
      <p>You can access <strong>Help</strong> at any time by clicking the <strong>Help</strong> button in the top bar.</p>
      <div class="controls">
        <p><strong>How to use Help:</strong></p>
        <p>📚 Click the <strong>Help</strong> button to open the tutorial panel</p>
        <p>📖 View your progress and completed tutorials</p>
        <p>🔄 Click any tutorial step to view it again</p>
        <p>❌ Close the panel anytime with the × button</p>
      </div>
    `;

    // Position bubble near the Help button (top-right area)
    bubble.style.top = '80px';
    bubble.style.right = '20px';
    bubble.style.left = 'auto';

    // Close button handler
    const closeBtn = bubble.querySelector('.close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        // Only mark as complete if not already completed
        if (!isCompleted) {
          this.completeStep(stepId);
        }
        this.hideBubble();
      });
    }

    this.overlay.appendChild(bubble);
    this.currentBubble = bubble;
  }

  private showViewDocsBubble(): void {
    this.hideBubble();

    if (!this.overlay) return;

    const bubble = document.createElement('div');
    bubble.className = 'help-bubble';

    const stepId = 'view-docs';
    const isCompleted = this.isStepCompleted(stepId);

    bubble.innerHTML = `
      <div class="close-btn" title="${isCompleted ? 'Close' : 'Mark as complete'}">×</div>
      <h3>View Documentation</h3>
      <p>Read the main documentation to learn the mechanics and your goals.</p>
      <div class="controls">
        <p><strong>Documentation:</strong></p>
        <p>📚 <a href="/docs.html" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline;">Open Documentation</a> (opens in new tab)</p>
      </div>
    `;

    // Position bubble (top-left area)
    bubble.style.top = '120px';
    bubble.style.left = '20px';

    // Close button handler
    const closeBtn = bubble.querySelector('.close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        // Only mark as complete if not already completed
        if (!isCompleted) {
          this.completeStep(stepId);
        }
        this.hideBubble();
      });
    }

    this.overlay.appendChild(bubble);
    this.currentBubble = bubble;
  }

  private showViewApiBubble(): void {
    this.hideBubble();

    if (!this.overlay) return;

    const bubble = document.createElement('div');
    bubble.className = 'help-bubble';

    const stepId = 'view-api';
    const isCompleted = this.isStepCompleted(stepId);

    bubble.innerHTML = `
      <div class="close-btn" title="${isCompleted ? 'Close' : 'Mark as complete'}">×</div>
      <h3>View API</h3>
      <p>The API documentation explains the functions and constants you can use in your code.</p>
      <div class="controls">
        <p><strong>API Documentation:</strong></p>
        <p>📚 <a href="/api.html" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline;">Open API Reference</a> (opens in new tab)</p>
      </div>
    `;

    // Position bubble (top-left area)
    bubble.style.top = '120px';
    bubble.style.left = '20px';

    // Close button handler
    const closeBtn = bubble.querySelector('.close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        // Only mark as complete if not already completed
        if (!isCompleted) {
          this.completeStep(stepId);
        }
        this.hideBubble();
      });
    }

    this.overlay.appendChild(bubble);
    this.currentBubble = bubble;
  }

  private hideBubble(): void {
    if (this.currentBubble) {
      this.currentBubble.remove();
      this.currentBubble = null;
    }
  }

  private updateQuestPanel(): void {
    if (!this.questList) return;

    this.questList.innerHTML = '';
    this.quests.forEach(parentQuest => {
      const parentCompleted = this.isParentQuestCompleted(parentQuest);

      const parentItem = document.createElement('div');
      parentItem.className = 'tutorial-quest-parent';
      if (parentCompleted) {
        parentItem.classList.add('completed');
      }
      parentItem.classList.add('expanded'); // Start expanded

      const header = document.createElement('div');
      header.className = 'tutorial-quest-parent-header';
      header.innerHTML = `
        <div class="quest-checkbox"></div>
        <div class="quest-text">${parentQuest.title}</div>
        <div class="expand-icon">▶</div>
      `;

      // Toggle expand/collapse
      header.addEventListener('click', () => {
        parentItem.classList.toggle('expanded');
      });

      const stepsContainer = document.createElement('div');
      stepsContainer.className = 'tutorial-quest-steps';

      parentQuest.steps.forEach(step => {
        const stepItem = document.createElement('div');
        stepItem.className = 'tutorial-quest-step';
        if (this.isStepCompleted(step.id)) {
          stepItem.classList.add('completed');
        }

        // Make step clickable
        stepItem.style.cursor = 'pointer';
        stepItem.title = 'Click to view tutorial';

        stepItem.innerHTML = `
          <div class="quest-checkbox"></div>
          <div class="quest-text">
            <strong>${step.title}</strong><br>
            <span style="font-size: 11px; color: #64748b;">${step.description}</span>
          </div>
        `;

        // Add click handler to show tutorial bubble
        stepItem.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent parent quest from collapsing/expanding
          this.showBubbleForStep(step.id);
        });

        stepsContainer.appendChild(stepItem);
      });

      parentItem.appendChild(header);
      parentItem.appendChild(stepsContainer);
      this.questList.appendChild(parentItem);
    });
  }
}

